/**
 * destinoDriveSync — Persistência de Destinos no Google Drive.
 *
 * Padrão: READ-MERGE-WRITE + write queue serializada + retry com backoff.
 * Segue o padrão de SumarioComentado/DriveSync.ts.
 *
 * O destinoData vive dentro do project.json.
 * Merge opera EXCLUSIVAMENTE no campo destinoData — nunca sobrescreve o resto.
 */

import { getTokenOrRefresh } from './googleAuth';
import { validateBeforeSave } from './googleDrive/core';
import { driveFetch, DriveConflictError } from './googleDrive/driveFetch';
import { etagCache } from './googleDrive/etagCache';
import { logger } from '../utils/logger';
import { stampSchema, migrateIfNeeded } from './googleDrive/schemaMigration';
import type { DestinoData } from '../types/destino';

const DRIVE_API_BASE = 'https://www.googleapis.com/drive/v3';
const BACKOFF_DELAYS = [1000, 3000, 8000];

// ---------- Write Queue (per projectId) ----------
const _writeLocks = new Map<string, Promise<void>>();

function withWriteLock<T>(projectId: string, fn: () => Promise<T>): Promise<T> {
  const prev = _writeLocks.get(projectId) ?? Promise.resolve();
  let resolve!: (value: T) => void;
  let reject!: (error: unknown) => void;
  const result = new Promise<T>((res, rej) => { resolve = res; reject = rej; });
  const next = prev.then(() => fn().then(resolve, reject), () => fn().then(resolve, reject));
  _writeLocks.set(projectId, next.then(() => {}, () => {}));
  return result;
}

// ---------- Token helper ----------
async function getToken(): Promise<string> {
  return getTokenOrRefresh();
}

class TokenExpiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TokenExpiredError';
  }
}

// ---------- Core API ----------

/**
 * Read project.json and extract destinoData.
 * Returns null if field doesn't exist (first use).
 */
export async function readDestinoData(projectFolderId: string): Promise<DestinoData | null> {
  const { data } = await readProjectJson(projectFolderId);
  const raw = (data.destinoData as DestinoData | undefined) ?? null;
  if (!raw) return null;
  const { data: migrated } = migrateIfNeeded(raw, 'destino');
  return migrated as DestinoData;
}

/**
 * Full READ-MERGE-WRITE cycle. Enqueued via write lock.
 */
export async function saveDestinoData(
  projectFolderId: string,
  destinoData: DestinoData
): Promise<void> {
  return withWriteLock(projectFolderId, async () => {
    await saveDestinoDataInternal(projectFolderId, destinoData, 0);
  });
}

// ---------- Internal implementation ----------

async function saveDestinoDataInternal(
  projectFolderId: string,
  destinoData: DestinoData,
  attempt: number
): Promise<void> {
  let currentAttempt = attempt;
  while (true) {
    try {
      // 1. READ project.json (captures ETag)
      const { data: snapshot, fileId } = await readProjectJson(projectFolderId);

      // 2. MERGE: replace ONLY destinoData (stamp schemaVersion)
      const merged = {
        ...snapshot,
        destinoData: stampSchema(destinoData),
        updatedAt: new Date().toISOString(),
      };

      // 3. VALIDATE: all original fields preserved
      validateMerge(snapshot, merged);

      // 4. VALIDATE serialization
      validateBeforeSave(merged, 'destinoDriveSync.saveDestinoData');

      // 5. WRITE (uses If-Match with cached ETag)
      await writeProjectJson(fileId, merged);
      return;
    } catch (error) {
      if (error instanceof TokenExpiredError) {
        throw error;
      }

      if (isRetryableError(error) && currentAttempt < BACKOFF_DELAYS.length) {
        await _delayFn(BACKOFF_DELAYS[currentAttempt]);
        currentAttempt++;
        continue;
      }

      logger.error('[destinoDriveSync]', 'Save failed after retries:', error);
      import('react-hot-toast').then(({ default: toast }) => {
        toast.error('Erro ao salvar destinos. Verifique sua conexão.');
      }).catch(() => {});
      throw error;
    }
  }
}

/**
 * Validate that merge preserved all original first-level fields.
 */
function validateMerge(
  snapshot: Record<string, unknown>,
  merged: Record<string, unknown>
): void {
  const snapshotKeys = Object.keys(snapshot);
  const missingKeys = snapshotKeys.filter((key) => !(key in merged));
  if (missingKeys.length > 0) {
    throw new Error(
      `[destinoDriveSync] Merge integrity check failed: missing keys [${missingKeys.join(', ')}]`
    );
  }
}

// ---------- Low-level Drive operations ----------

async function readProjectJson(projectFolderId: string): Promise<{ data: Record<string, unknown>; fileId: string }> {
  const token = await getToken();

  const query = `name='project.json' and '${projectFolderId}' in parents and trashed=false`;
  const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id)`;

  const searchResponse = await driveFetch(searchUrl, {
    headers: { Authorization: `Bearer ${token}` },
  });

  handleHttpError(searchResponse);
  const searchData = await searchResponse.json();

  if (!searchData.files || searchData.files.length === 0) {
    throw new Error('project.json not found');
  }

  const fileId = searchData.files[0].id;
  const downloadUrl = `${DRIVE_API_BASE}/files/${fileId}?alt=media`;
  const downloadResponse = await driveFetch(downloadUrl, {
    headers: { Authorization: `Bearer ${token}` },
  });

  handleHttpError(downloadResponse);

  // Capture ETag for If-Match on subsequent writes
  const etag = downloadResponse.headers.get('ETag');
  if (etag) etagCache.set(fileId, etag);

  const text = await downloadResponse.text();

  try {
    return { data: JSON.parse(text), fileId };
  } catch {
    throw new Error('project.json contains invalid JSON');
  }
}

async function writeProjectJson(
  fileId: string,
  data: Record<string, unknown>
): Promise<void> {
  const token = await getToken();

  const patchUrl = `https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`;
  const cachedEtag = etagCache.get(fileId);
  const patchResponse = await driveFetch(patchUrl, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(cachedEtag ? { 'If-Match': cachedEtag } : {}),
    },
    body: JSON.stringify(data, null, 2),
  });

  if (patchResponse.status === 412) {
    etagCache.delete(fileId);
    throw new DriveConflictError('project.json');
  }

  handleHttpError(patchResponse);

  // Update ETag cache with new value
  const newEtag = patchResponse.headers.get('ETag');
  if (newEtag) etagCache.set(fileId, newEtag);
}

// ---------- Helpers ----------

function handleHttpError(response: Response): void {
  if (response.ok) return;
  if (response.status === 401 || response.status === 403) {
    throw new TokenExpiredError('Sessão expirada. Faça login novamente.');
  }
  if (response.status === 429 || response.status === 503) {
    const err = new Error(`HTTP ${response.status}: ${response.statusText}`);
    (err as RetryableHttpError).retryable = true;
    throw err;
  }
  throw new Error(`HTTP ${response.status}: ${response.statusText}`);
}

interface RetryableHttpError extends Error {
  retryable: boolean;
}

function isRetryableError(error: unknown): boolean {
  if (error && typeof error === 'object' && 'retryable' in error) {
    return (error as RetryableHttpError).retryable === true;
  }
  return false;
}

let _delayFn = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

// ---------- Debounce ----------
const _debounceTimers = new Map<string, ReturnType<typeof setTimeout>>();
const _pendingGetData = new Map<string, { projectFolderId: string; getDataFn: () => DestinoData; onError?: (error: unknown) => void }>();

/**
 * Debounced save (1s for discrete actions like create/rename/delete/assign).
 * getDataFn is called when the timer fires to get the latest state.
 */
export function debouncedSaveDestinos(
  projectFolderId: string,
  getDataFn: () => DestinoData,
  onError?: (error: unknown) => void
): void {
  const key = `destino-save-${projectFolderId}`;
  const existing = _debounceTimers.get(key);
  if (existing) clearTimeout(existing);

  _pendingGetData.set(key, { projectFolderId, getDataFn, onError });

  const timer = setTimeout(() => {
    _debounceTimers.delete(key);
    const pending = _pendingGetData.get(key);
    _pendingGetData.delete(key);
    if (!pending) return;
    const data = pending.getDataFn();
    saveDestinoData(projectFolderId, data).catch((error) => {
      if (!(error instanceof TokenExpiredError)) {
        logger.error('[destinoDriveSync]', 'Debounced save failed:', error);
        import('react-hot-toast').then(({ default: toast }) => {
          toast.error('Erro ao salvar destinos. Verifique sua conexão.');
        }).catch(() => {});
        pending.onError?.(error);
      }
    });
  }, 1000);

  _debounceTimers.set(key, timer);
}

/**
 * Flush all pending debounced saves immediately. Called on beforeunload.
 * Fire-and-forget — uses fetch keepalive for best-effort delivery.
 */
export function flushDestinoSync(): void {
  for (const [key, timer] of _debounceTimers) {
    clearTimeout(timer);
    _debounceTimers.delete(key);
    const pending = _pendingGetData.get(key);
    _pendingGetData.delete(key);
    if (!pending) continue;
    const data = pending.getDataFn();
    saveDestinoData(pending.projectFolderId, data).catch((error) => {
      if (!(error instanceof TokenExpiredError)) {
        logger.error('[destinoDriveSync]', 'Flush save failed:', error);
        import('react-hot-toast').then(({ default: toast }) => {
          toast.error('Erro ao salvar destinos. Verifique sua conexão.');
        }).catch(() => {});
      }
    });
  }
}

/**
 * Immediate save — used on project switch or window close.
 */
export function immediateSaveDestinos(
  projectFolderId: string,
  getDataFn: () => DestinoData
): Promise<void> {
  const key = `destino-save-${projectFolderId}`;
  const existing = _debounceTimers.get(key);
  if (existing) {
    clearTimeout(existing);
    _debounceTimers.delete(key);
  }

  const data = getDataFn();
  return saveDestinoData(projectFolderId, data);
}

/**
 * Cancel all pending debounce timers.
 */
export function cancelPendingDestinoSaves(): void {
  for (const timer of _debounceTimers.values()) {
    clearTimeout(timer);
  }
  _debounceTimers.clear();
}

// Export internals for testing
export const _testing = {
  withWriteLock,
  readProjectJson,
  writeProjectJson,
  validateMerge,
  BACKOFF_DELAYS,
  getToken,
  saveDestinoDataInternal,
  setDelayFn: (fn: (ms: number) => Promise<void>) => { _delayFn = fn; },
  resetDelayFn: () => { _delayFn = (ms) => new Promise((r) => setTimeout(r, ms)); },
};
